import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import { fmtLocalDate } from '../../../utils/datetime';
import type { DailyQcLimit } from './dailyQcApi';
import { fmtNum, opText, seriesStats, type TrendPoint } from './dailyQcFormat';

const OK = '#16a36a';
const BAD = '#df5148';
const LINE = '#f97316';

/** Y range covering the values AND the limit lines, padded 15% like the prototype. */
function domainOf(pts: TrendPoint[], limits: DailyQcLimit[]): [number, number] {
  const all = pts.map((p) => p.v).concat(limits.map((l) => l.value));
  let lo = Math.min(...all);
  let hi = Math.max(...all);
  const pad = (hi - lo || Math.abs(hi) * 0.05 || 1) * 0.15;
  lo -= pad; hi += pad;
  return [Number(lo.toPrecision(4)), Number(hi.toPrecision(4))];
}

function dotColour(p: TrendPoint): string {
  if (p.current) return LINE;
  if (p.ok === false) return BAD;
  if (p.ok === true) return OK;
  return LINE;
}

interface DotProps { cx?: number; cy?: number; payload?: TrendPoint; spark?: boolean }

/** Recharts hands the dot renderer its props WITH a `key` — pass the key directly, never spread it. */
function renderDot(props: unknown, spark?: boolean) {
  const { key, cx, cy, payload } = props as DotProps & { key?: string };
  return <PointDot key={key} cx={cx} cy={cy} payload={payload} spark={spark} />;
}
function PointDot({ cx, cy, payload, spark }: DotProps) {
  if (cx === undefined || cy === undefined || !payload) return null;
  const r = spark ? (payload.current ? 4 : 2) : (payload.ok === false ? 5 : 3.5);
  return (
    <circle cx={cx} cy={cy} r={r} fill={dotColour(payload)} stroke="#fff" strokeWidth={payload.current ? 1.5 : 1}>
      {/* The prototype's hover text: the entry date (or "This entry") and the value. */}
      <title>{`${payload.current ? 'This entry' : fmtLocalDate(payload.d)}: ${fmtNum(payload.v)}`}</title>
    </circle>
  );
}

/** Five evenly spaced grid levels across the domain, as the prototype draws them. */
function gridTicks([lo, hi]: [number, number]): number[] {
  return [0, 1, 2, 3, 4].map((k) => lo + ((hi - lo) * k) / 4);
}

/** The points that get a date under them — every ⌈n/12⌉-th one plus the last (prototype). */
function dateTicks(n: number): number[] {
  const every = Math.max(1, Math.ceil(n / 12));
  return Array.from({ length: n }, (_, i) => i).filter((i) => i % every === 0 || i === n - 1);
}

/** History trend: the field's values over the date range, mean line, red dashed limit lines. */
export function DailyQcTrendChart({ points, limits }: { points: TrendPoint[]; limits: DailyQcLimit[] }) {
  if (points.length === 0) return <div className="dqc-trend-empty">No values in this date range.</div>;
  const stats = seriesStats(points)!;
  const data = points.map((p, i) => ({ ...p, i }));
  const domain = domainOf(points, limits);
  // A single point sits in the middle, as the prototype centres it.
  const xDomain: [number, number] = data.length === 1 ? [-1, 1] : [0, data.length - 1];
  return (
    <div className="dqc-trend-chart">
      <ResponsiveContainer width="100%" height={230}>
        <LineChart data={data} margin={{ top: 16, right: 70, bottom: 4, left: 4 }}>
          <CartesianGrid stroke="#eef1f5" vertical={false} />
          <XAxis dataKey="i" type="number" domain={xDomain} ticks={dateTicks(data.length)} interval={0}
            tickFormatter={(i: number) => fmtLocalDate(data[Math.round(i)]?.d).slice(0, 5)}
            tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
          <YAxis domain={domain} ticks={gridTicks(domain)} interval={0} tick={{ fontSize: 10, fill: '#64748b' }} width={48}
            tickFormatter={(v: number) => fmtNum(Number(v.toPrecision(4)))} axisLine={false} tickLine={false} />
          <ReferenceLine y={stats.mean} stroke="#94a3b8" strokeDasharray="2 3"
            label={{ value: `Mean ${fmtNum(stats.mean)}`, position: 'right', fontSize: 10, fill: '#64748b' }} />
          {limits.map((l) => (
            <ReferenceLine key={`${l.op}${l.value}`} y={l.value} stroke={BAD} strokeDasharray="5 4"
              label={{ value: `${opText(l.op)} ${l.value}`, position: 'right', fontSize: 10, fill: BAD }} />
          ))}
          <Line dataKey="v" stroke={LINE} strokeWidth={2} isAnimationActive={false}
            dot={(p: unknown) => renderDot(p)} activeDot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/** In-form sparkline: the last 15 results plus the value being entered (orange). */
export function DailyQcSparkline({ points, limits }: { points: TrendPoint[]; limits: DailyQcLimit[] }) {
  if (points.length === 0) return <div className="dqc-spark" />;
  const data = points.map((p, i) => ({ ...p, i }));
  return (
    <div className="dqc-spark" aria-hidden="true">
      <ResponsiveContainer width="100%" height={30}>
        <LineChart data={data} margin={{ top: 5, right: 6, bottom: 5, left: 4 }}>
          <XAxis dataKey="i" type="number" hide domain={data.length === 1 ? [-1, 1] : [0, data.length - 1]} />
          <YAxis hide domain={domainOf(points, limits)} />
          {limits.map((l) => <ReferenceLine key={`${l.op}${l.value}`} y={l.value} stroke={BAD} strokeDasharray="5 4" />)}
          <Line dataKey="v" stroke="#94a3b8" strokeWidth={1.2} isAnimationActive={false}
            dot={(p: unknown) => renderDot(p, true)} activeDot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
